import { makeAutoObservable } from 'mobx'
import { hexToRgb } from 'utils/colorHelpers'
import { HandbookTheme } from '../utils/themeUtils'
import {
  Hue,
  Lightness,
  Saturation,
  Alpha,
  ThemePreset,
  Overrides,
  HSLSpec,
  ThemeMode,
  ITheme,
  ThemeCSSProperties,
} from './Theme.types'
import { pick } from 'lodash'

export class Theme {
  private _preset: ThemePreset
  private _hue?: Hue
  private _overrides?: Overrides

  static fromLegacyTheme = (legacyThemeName: HandbookTheme): Theme => {
    return new Theme(LEGACY_THEME_MAPPING[legacyThemeName])
  }

  static isNull = (theme: ITheme) => {
    return !theme.hue && !theme.preset && !theme.overrides
  }

  constructor({ hue, preset, overrides }: ITheme = {}) {
    makeAutoObservable(this)
    this._hue = hue || undefined
    this._preset = preset || ThemePreset.Light
    this._overrides = overrides || undefined
  }

  get hue() {
    return this._hue
  }

  set hue(hue: number | undefined) {
    this._hue = hue
  }

  get preset() {
    return this._preset
  }

  set preset(preset: ThemePreset) {
    this._preset = preset
  }

  get overrides() {
    return this._overrides
  }

  set overrides(overrides: Overrides | undefined) {
    this._overrides = overrides
  }

  toJSON = (): ITheme => {
    return {
      hue: this._hue,
      preset: this._preset,
      overrides: this._overrides,
    }
  }

  reset = () => {
    this.hue = undefined
    this.preset = ThemePreset.Light
    this.overrides = undefined
  }

  load = (config: ITheme) => {
    this.hue = config.hue || undefined
    this.preset = config.preset || ThemePreset.Light
    this.overrides = config.overrides || undefined
  }

  get palette(): ThemeCSSProperties {
    if (this.overrides) {
      return this.customPalette
    }

    return this.palettes[this.preset]
  }

  get palettes() {
    if (this.hue === undefined) {
      return this.almanacPalettes
    }
    return this.presetPalettes
  }

  paletteProperties(properties: string[]): ThemeCSSProperties {
    return pick(this.palette, properties)
  }

  hsl = (baseS: Saturation, baseL: Lightness, baseA: Alpha, ...specs: HSLSpec[]) => {
    if (!this.hue) {
      return undefined
    }

    let s = baseS
    let l = baseL
    let a = baseA

    const spec = specs.find((o) => {
      if (!this.hue) {
        return false
      }
      let [start, end] = [o[0]].flat()
      if (end === undefined) {
        end = start
      }
      return start <= this.hue && end >= this.hue
    })

    if (spec) {
      s = spec[1]
      l = spec[2]
      a = spec[3]
    }

    return `hsl(${this.hue}, ${s * 100}%, ${l * 100}%, ${a})`
  }

  get mode(): ThemeMode {
    if (this.overrides) {
      const { r, g, b } = hexToRgb(this.overrides.sidebarBackgroundColor) || { r: 255, g: 255, b: 255 }
      const l = r * 0.299 + g * 0.587 + b * 0.114
      return l > 155 ? ThemeMode.Light : ThemeMode.Dark
    }

    if (this.preset === ThemePreset.Light) {
      return ThemeMode.Light
    }
    return ThemeMode.Dark
  }

  get defaultOverrides(): Overrides {
    return {
      sidebarBackgroundColor: '#FFFFFF',
      actionColor: '#664EFF',
      sidebarIconsColor: '#8F98A9',
      sidebarTextColor: '#4C566A',
      sidebarSelectedBackgroundColor: '#F0FAFF',
      sidebarSelectedTextColor: '#007CC1',
      sidebarHoverBackgroundColor: '#F2F3F5',
      sidebarHoverTextColor: '#4C566A',
    }
  }

  get almanacPalettes(): { [key in ThemePreset]: ThemeCSSProperties } {
    return {
      [ThemePreset.Light]: {} as ThemeCSSProperties,

      [ThemePreset.Saturated]: {
        // Base variables
        '--neutral-0': 'hsl(220, 16%, 36%, 100%)',
        '--neutral-5': 'hsl(0, 0%, 100%, 8%)',
        '--neutral-10': 'hsl(0, 0%, 100%, 15%)',
        '--neutral-50': 'hsl(0, 0%, 100%, 100%)',
        '--neutral-90': 'hsl(0, 0%, 100%, 100%)',
        '--focus-50': 'hsl(0, 0%, 100%, 100%)',
        '--action-50': '#664eff',
        '--action-60': '#523ae2',
        // Sidebar variables
        '--glass-sidebar-background-color': 'hsl(220, 16%, 39%, 0.9)',
        '--sidebar-background-color': '#4C566A',
        '--nav-item-icon-color': '#ffffff',
        '--nav-item-color': '#ffffff',
        '--nav-item-control-color': '#ffffff',
        '--nav-item-control-icon-color': '#ffffff',
        '--nav-item-section-header-color': '#ffffff',
        '--nav-item-icon-color-hover': '#ffffff',
        '--nav-item-color-hover': '#ffffff',
        '--nav-item-background-color-hover': 'hsl(0, 0%, 100%, 0.08)',
        '--nav-item-control-color-hover': '#ffffff',
        '--nav-item-icon-color-active': '#ffffff',
        '--nav-item-color-active': '#ffffff',
        '--nav-item-background-color-active': 'hsl(0, 0%, 100%, 0.15)',
        '--nav-item-control-color-active': '#ffffff',
        '--notifications-active-icon-color': '#ffffff',

        '--override-menu-item-color': 'var(--nav-item-color)',
        '--override-menu-item-background-color-hover': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-color-active': 'var(--nav-item-color-active)',
        '--override-menu-item-background-color-active': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-active-hover': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-focus': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-icon-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-checkbox-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-chevron-color': 'var(--nav-item-icon-color)',
        '--override-menu-section-header-color': 'var(--nav-item-color)',
      },

      [ThemePreset.Dark]: {
        // Base variables
        '--neutral-0': 'hsl(224, 25%, 12%, 100%)',
        '--neutral-5': 'hsl(0, 0%, 100%, 5%)',
        '--neutral-10': 'hsl(0, 0%, 100%, 10%)',
        '--neutral-50': 'hsl(0, 0%, 90%, 100%)',
        '--neutral-90': 'hsl(0, 0%, 90%, 100%)',
        '--focus-50': 'hsl(0, 0%, 100%, 100%)',
        '--action-50': '#664eff',
        '--action-60': '#523ae2',
        // Sidebar variables
        '--glass-sidebar-background-color': 'hsl(224, 25%, 22%, 0.9)',
        '--sidebar-background-color': '#161A25',
        '--nav-item-icon-color': '#ffffff',
        '--nav-item-color': '#ffffff',
        '--nav-item-control-color': '#ffffff',
        '--nav-item-control-icon-color': '#ffffff',
        '--nav-item-section-header-color': '#ffffff',
        '--nav-item-icon-color-hover': '#ffffff',
        '--nav-item-color-hover': '#ffffff',
        '--nav-item-background-color-hover': 'hsl(0, 0%, 100%, 0.08)',
        '--nav-item-control-color-hover': '#ffffff',
        '--nav-item-icon-color-active': '#ffffff',
        '--nav-item-color-active': '#ffffff',
        '--nav-item-background-color-active': 'hsl(0, 0%, 100%, 0.15)',
        '--nav-item-control-color-active': '#ffffff',
        '--notifications-active-icon-color': '#ffffff',

        '--override-menu-item-color': 'var(--nav-item-color)',
        '--override-menu-item-background-color-hover': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-color-active': 'var(--nav-item-color-active)',
        '--override-menu-item-background-color-active': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-active-hover': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-focus': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-icon-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-checkbox-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-chevron-color': 'var(--nav-item-icon-color)',
        '--override-menu-section-header-color': 'var(--nav-item-color)',
      },
    }
  }

  get presetPalettes(): { [key in ThemePreset]: ThemeCSSProperties } {
    return {
      [ThemePreset.Light]: {
        // Base variables
        '--neutral-0': this.hsl(0.85, 0.98, 1),
        '--neutral-5': this.hsl(1, 0.4, 0.05),
        '--neutral-10': this.hsl(0.65, 0.3, 0.1),
        '--neutral-30': this.hsl(0.8, 0.2, 0.75),
        '--neutral-50': this.hsl(0.8, 0.2, 0.75),
        '--neutral-90': this.hsl(0.8, 0.2, 1),
        '--focus-50': this.hsl(0.8, 0.2, 1),
        '--action-50': this.hsl(0.9, 0.35, 1),
        '--action-60': this.hsl(1, 0.25, 1),
        // Sidebar Variables
        '--glass-sidebar-background-color': this.hsl(0.68, 0.98, 0.9),
        '--sidebar-background-color': this.hsl(0.85, 0.98, 1),
        '--nav-item-icon-color': this.hsl(0.8, 0.2, 0.75),
        '--nav-item-color': this.hsl(0.8, 0.2, 1),
        '--nav-item-control-color': this.hsl(0.8, 0.2, 0.75),
        '--nav-item-control-icon-color': this.hsl(0.8, 0.2, 0.75),
        '--nav-item-section-header-color': this.hsl(0.8, 0.2, 0.75),
        '--nav-item-icon-color-hover': this.hsl(0.8, 0.2, 0.75),
        '--nav-item-color-hover': this.hsl(0.8, 0.2, 1),
        '--nav-item-background-color-hover': this.hsl(1, 0.4, 0.05),
        '--nav-item-control-color-hover': this.hsl(0.8, 0.2, 1),
        '--nav-item-icon-color-active': this.hsl(0.8, 0.3, 1),
        '--nav-item-color-active': this.hsl(0.8, 0.2, 1),
        '--nav-item-background-color-active': this.hsl(1, 0.4, 0.1),
        '--nav-item-control-color-active': this.hsl(0.8, 0.3, 1),

        '--override-menu-item-color': 'var(--nav-item-color)',
        '--override-menu-item-background-color-hover': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-color-active': 'var(--nav-item-color-active)',
        '--override-menu-item-background-color-active': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-active-hover': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-focus': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-icon-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-checkbox-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-chevron-color': 'var(--nav-item-icon-color)',
        '--override-menu-section-header-color': 'var(--nav-item-color)',
      },

      [ThemePreset.Saturated]: {
        // Base variables
        '--neutral-0': this.hsl(0.5, 0.43, 1, [[25, 166], 0.5, 0.35, 1]),
        '--neutral-5': this.hsl(0, 1, 0.08),
        '--neutral-10': this.hsl(0, 1, 0.15),
        '--neutral-30': this.hsl(0, 1, 0.8),
        '--neutral-50': this.hsl(0, 1, 1),
        '--neutral-90': this.hsl(0, 1, 1),
        '--focus-50': this.hsl(0, 1, 1),
        '--action-50': this.hsl(0.9, 0.35, 1),
        '--action-60': this.hsl(1, 0.25, 1),
        // Sidebar variables
        '--glass-sidebar-background-color': this.hsl(0.4, 0.38, 0.9),
        '--sidebar-background-color': this.hsl(0.5, 0.43, 1, [[25, 166], 0.5, 0.35, 1]),
        '--nav-item-icon-color': this.hsl(0, 1, 0.8),
        '--nav-item-color': this.hsl(0, 1, 1),
        '--nav-item-control-color': this.hsl(0, 1, 1),
        '--nav-item-control-icon-color': this.hsl(0, 1, 0.8),
        '--nav-item-section-header-color': this.hsl(0, 1, 0.8),
        '--nav-item-icon-color-hover': this.hsl(0, 1, 1),
        '--nav-item-color-hover': this.hsl(0, 1, 1),
        '--nav-item-background-color-hover': this.hsl(0, 1, 0.08),
        '--nav-item-control-color-hover': this.hsl(0, 1, 1),
        '--nav-item-icon-color-active': this.hsl(0, 1, 1),
        '--nav-item-color-active': this.hsl(0, 1, 1),
        '--nav-item-background-color-active': this.hsl(0, 1, 0.15),
        '--nav-item-control-color-active': this.hsl(0, 1, 1),
        '--notifications-active-icon-color': this.hsl(0, 1, 1),

        '--override-menu-item-color': 'var(--nav-item-color)',
        '--override-menu-item-background-color-hover': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-color-active': 'var(--nav-item-color-active)',
        '--override-menu-item-background-color-active': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-active-hover': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-focus': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-icon-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-checkbox-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-chevron-color': 'var(--nav-item-icon-color)',
        '--override-menu-section-header-color': 'var(--nav-item-color)',
      },

      [ThemePreset.Dark]: {
        // Base variables
        '--neutral-0': this.hsl(0.9, 0.15, 1),
        '--neutral-5': this.hsl(0, 1, 0.05),
        '--neutral-10': this.hsl(0, 1, 0.1),
        '--neutral-30': this.hsl(0.9, 0.9, 1),
        '--neutral-50': this.hsl(0, 1, 1),
        '--neutral-90': this.hsl(0, 1, 1),
        '--focus-50': this.hsl(0, 1, 1),
        '--action-50': this.hsl(0.9, 0.4, 1),
        '--action-60': this.hsl(1, 0.3, 1),
        // Sidebar variables
        '--glass-sidebar-background-color': this.hsl(0.48, 0.23, 0.9),
        '--sidebar-background-color': this.hsl(0.9, 0.15, 1),
        '--nav-item-icon-color': this.hsl(0.9, 0.9, 1),
        '--nav-item-color': this.hsl(0.9, 0.9, 1),
        '--nav-item-control-color': this.hsl(0.9, 0.9, 1),
        '--nav-item-control-icon-color': this.hsl(0.9, 0.9, 1),
        '--nav-item-section-header-color': this.hsl(0.9, 0.9, 1),
        '--nav-item-icon-color-hover': this.hsl(0.9, 0.9, 1),
        '--nav-item-color-hover': this.hsl(0.9, 0.9, 1),
        '--nav-item-background-color-hover': this.hsl(0, 1, 0.05),
        '--nav-item-control-color-hover': this.hsl(0.9, 0.9, 1),
        '--nav-item-icon-color-active': this.hsl(0, 1, 1),
        '--nav-item-color-active': this.hsl(0, 1, 1),
        '--nav-item-background-color-active': this.hsl(0, 1, 0.1),
        '--nav-item-control-color-active': this.hsl(0, 1, 1),
        '--notifications-active-icon-color': this.hsl(0, 1, 1),

        '--override-menu-item-color': 'var(--nav-item-color)',
        '--override-menu-item-background-color-hover': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-color-active': 'var(--nav-item-color-active)',
        '--override-menu-item-background-color-active': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-active-hover': 'var(--nav-item-background-color-active)',
        '--override-menu-item-background-color-focus': 'var(--nav-item-background-color-hover)',
        '--override-menu-item-icon-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-checkbox-color': 'var(--nav-item-icon-color)',
        '--override-menu-item-chevron-color': 'var(--nav-item-icon-color)',
        '--override-menu-section-header-color': 'var(--nav-item-color)',
      },
    }
  }

  get customPalette(): ThemeCSSProperties {
    if (!this.overrides) {
      return {}
    }

    return {
      // Base variables
      '--action-50': this.overrides.actionColor,
      '--action-60': this.overrides.actionColor,
      '--neutral-0': this.overrides.sidebarBackgroundColor,
      '--neutral-5': this.overrides.sidebarHoverBackgroundColor,
      '--neutral-10': this.overrides.sidebarSelectedBackgroundColor,
      '--neutral-30': this.overrides.sidebarSelectedTextColor,
      '--neutral-50': this.overrides.sidebarTextColor,
      '--neutral-90': this.overrides.sidebarTextColor,
      '--focus-50': this.overrides.sidebarSelectedTextColor,
      // Sidebar variables
      '--sidebar-background-color': this.overrides.sidebarBackgroundColor,
      '--nav-item-icon-color': this.overrides.sidebarIconsColor,
      '--nav-item-color': this.overrides.sidebarTextColor,
      '--nav-item-control-color': this.overrides.sidebarIconsColor,
      '--nav-item-control-icon-color': this.overrides.sidebarIconsColor,
      '--nav-item-section-header-color': this.overrides.sidebarTextColor,
      '--nav-item-icon-color-hover': this.overrides.sidebarHoverTextColor,
      '--nav-item-color-hover': this.overrides.sidebarHoverTextColor,
      '--nav-item-background-color-hover': this.overrides.sidebarHoverBackgroundColor,
      '--nav-item-control-color-hover': this.overrides.sidebarHoverTextColor,
      '--nav-item-icon-color-active': this.overrides.sidebarSelectedTextColor,
      '--nav-item-color-active': this.overrides.sidebarSelectedTextColor,
      '--nav-item-background-color-active': this.overrides.sidebarSelectedBackgroundColor,
      '--nav-item-control-color-active': this.overrides.sidebarSelectedTextColor,
      '--notifications-active-icon-color': this.overrides.actionColor,
    }
  }
}

enum LegacyThemeHue {
  Blue = 234,
  Aqua = 194,
  Green = 130,
  Yellow = 25,
  Orange = 25,
  Red = 8,
  Pink = 332,
  Purple = 284,
}

const LEGACY_THEME_MAPPING: { [key in HandbookTheme]: ITheme } = {
  neutral40: { hue: undefined, preset: ThemePreset.Dark },

  blue40: { hue: LegacyThemeHue.Blue, preset: ThemePreset.Dark },
  aqua40: { hue: LegacyThemeHue.Aqua, preset: ThemePreset.Dark },
  green40: { hue: LegacyThemeHue.Green, preset: ThemePreset.Dark },
  yellow40: { hue: LegacyThemeHue.Yellow, preset: ThemePreset.Saturated },
  orange40: { hue: LegacyThemeHue.Orange, preset: ThemePreset.Dark },
  red40: { hue: LegacyThemeHue.Red, preset: ThemePreset.Dark },
  pink40: { hue: LegacyThemeHue.Pink, preset: ThemePreset.Dark },
  purple40: { hue: LegacyThemeHue.Purple, preset: ThemePreset.Dark },

  neutral30: { hue: undefined, preset: ThemePreset.Dark },
  blue30: { hue: LegacyThemeHue.Blue, preset: ThemePreset.Saturated },
  aqua30: { hue: LegacyThemeHue.Aqua, preset: ThemePreset.Saturated },
  green30: { hue: LegacyThemeHue.Green, preset: ThemePreset.Dark },
  yellow30: { hue: LegacyThemeHue.Yellow, preset: ThemePreset.Saturated },
  orange30: { hue: LegacyThemeHue.Orange, preset: ThemePreset.Saturated },
  red30: { hue: LegacyThemeHue.Red, preset: ThemePreset.Saturated },
  pink30: { hue: LegacyThemeHue.Pink, preset: ThemePreset.Saturated },
  purple30: { hue: LegacyThemeHue.Purple, preset: ThemePreset.Saturated },

  neutral20: { hue: undefined, preset: ThemePreset.Saturated },
  blue20: { hue: LegacyThemeHue.Aqua, preset: ThemePreset.Saturated },
  aqua20: { hue: LegacyThemeHue.Green, preset: ThemePreset.Saturated },
  green20: { hue: LegacyThemeHue.Green, preset: ThemePreset.Saturated },
  yellow20: { hue: LegacyThemeHue.Yellow, preset: ThemePreset.Saturated },
  orange20: { hue: LegacyThemeHue.Orange, preset: ThemePreset.Saturated },
  red20: { hue: LegacyThemeHue.Red, preset: ThemePreset.Saturated },
  pink20: { hue: LegacyThemeHue.Pink, preset: ThemePreset.Saturated },
  purple20: { hue: LegacyThemeHue.Purple, preset: ThemePreset.Saturated },

  neutral10: { hue: undefined, preset: ThemePreset.Light },
  blue10: { hue: LegacyThemeHue.Blue, preset: ThemePreset.Light },
  aqua10: { hue: LegacyThemeHue.Aqua, preset: ThemePreset.Light },
  green10: { hue: LegacyThemeHue.Green, preset: ThemePreset.Light },
  yellow10: { hue: LegacyThemeHue.Yellow, preset: ThemePreset.Light },
  orange10: { hue: LegacyThemeHue.Orange, preset: ThemePreset.Light },
  red10: { hue: LegacyThemeHue.Red, preset: ThemePreset.Light },
  pink10: { hue: LegacyThemeHue.Pink, preset: ThemePreset.Light },
  purple10: { hue: LegacyThemeHue.Purple, preset: ThemePreset.Light },

  noTheme: { hue: undefined, preset: ThemePreset.Light },
  greyBlueGradient: { hue: LegacyThemeHue.Blue, preset: ThemePreset.Light },
  blueGreenGradient: { hue: LegacyThemeHue.Blue, preset: ThemePreset.Light },
  greenYellowGradient: { hue: LegacyThemeHue.Green, preset: ThemePreset.Light },
  yellowOrangeGradient: { hue: LegacyThemeHue.Yellow, preset: ThemePreset.Light },
  orangeRedGradient: { hue: LegacyThemeHue.Red, preset: ThemePreset.Light },
  redPinkGradient: { hue: LegacyThemeHue.Pink, preset: ThemePreset.Light },
  pinkPurpleGradient: { hue: LegacyThemeHue.Purple, preset: ThemePreset.Light },
  purpleBlueGradient: { hue: LegacyThemeHue.Purple, preset: ThemePreset.Light },
}
